import React, { ReactNode } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import * as actions from '../../actions';
import { styles } from './MobileDesignConfig.styles';
import { Button, Viewer, Paper, Spinner } from '..';
import {
  MobileDesignConfigClasses,
  MobileDesignConfigMappedProps,
  MobileDesignConfigView,
  MobileDesignProduct
} from './MobileDesignConfig.types';
import { DesignContent } from './components/DesignContent';
import { SplashColorSection } from './components/SplashColorSection';
import { useMobileDesignConfig } from './hooks/useMobileDesignConfig';

interface ViewerProps {
  value: number;
  onChange: (value: number) => void;
  buttonList: string[];
}

interface SpinnerProps {
  className?: string;
}

const ViewerComponent = Viewer as unknown as React.ComponentType<ViewerProps>;
const SpinnerComponent = Spinner as unknown as React.ComponentType<SpinnerProps>;

interface OwnProps {
  config: string;
  selected: number;
  onClick: (value: number) => void;
  renderFirstStepDesign: ReactNode;
  renderStyle: ReactNode;
}

interface FirebaseRealTimeState {
  firebaseRealTime?: {
    generalConfig?: {
      images?: {
        myLoyalty?: unknown;
        slides?: string[];
        myAccount?: unknown;
        myOrders?: unknown;
      };
      socials?: {
        facebook?: string | null;
        instagram?: string | null;
        linkedin?: string | null;
      };
      mobileApp?: {
        titleDescription?: string;
        clientSpace?: string;
        enabled?: boolean;
        newProductsDescription?: string;
        splashBackgroundColor?: string;
      };
      url?: string | null;
      newProducts?: Record<string, MobileDesignProduct>;
      loyalty?: {
        name?: string;
      };
    };
  };
}

const mapStateToProps = ({ firebaseRealTime }: FirebaseRealTimeState): MobileDesignConfigMappedProps => {
  const { generalConfig = {} } = firebaseRealTime || {};
  const {
    images = {},
    socials = {},
    mobileApp = {},
    url,
    newProducts = {},
    loyalty = {}
  } = generalConfig;
  const { titleDescription, clientSpace, enabled, newProductsDescription, splashBackgroundColor } =
    mobileApp;
  const { facebook, instagram, linkedin } = socials;
  const { myLoyalty, slides, myAccount, myOrders } = images;
  const { name } = loyalty;

  return {
    facebook,
    instagram,
    linkedin,
    titleDescription,
    clientSpace,
    myLoyalty,
    slides,
    url,
    newProducts,
    name,
    newProductsDescription,
    myAccount,
    myOrders,
    enabled,
    splashBackgroundColor
  };
};

const connector = connect(mapStateToProps, actions);

type ReduxProps = ConnectedProps<typeof connector>;

type Props = OwnProps & ReduxProps & WithStyles<typeof styles> & RouteComponentProps;

const MobileDesignConfigComponent: React.FC<Props> = props => {
  const { classes, config, selected, onClick, renderFirstStepDesign, renderStyle } = props;
  const typedClasses = classes as MobileDesignConfigClasses;
  const {
    state,
    searchTerm,
    colorState,
    sectionsForDisplay,
    handleSave,
    handleSliderImageChange,
    handleSliderImageRemove,
    handleDesignImageUpload,
    handleDesignImageRemove,
    handleDisableLoader,
    handleInputChange,
    handleResetValue,
    handleLinkChange,
    handleSocialChange,
    onProductCheck,
    onSearchChange,
    applySearchFilter,
    handleSplashInputChange,
    handleSplashPickerChange,
    toggleSplashColorPicker,
    closeSplashColorPicker,
    mediaState
  } = useMobileDesignConfig(props);
  const currentView =
    (config as MobileDesignConfigView) ?? MobileDesignConfigView.NONE;
  const isDefaultView = currentView === MobileDesignConfigView.NONE;

  return (
    <>
      {isDefaultView ? (
        <>
          <Paper className={classes.paperStyle}>
            <ViewerComponent
              value={selected}
              onChange={onClick}
              buttonList={['Config', 'Style']}
            />
          </Paper>
          <div className={classes.container}>
            {selected === 0 ? (
              <div>{renderFirstStepDesign}</div>
            ) : (
              <div className="p-3">
                {renderStyle}
                <SplashColorSection
                  classes={typedClasses}
                  colorState={colorState}
                  onTogglePicker={toggleSplashColorPicker}
                  onInputChange={handleSplashInputChange}
                  onPickerChange={handleSplashPickerChange}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        <div className={`p-4 ${classes.containerStyle}`}>
          <DesignContent
            view={currentView}
            classes={typedClasses}
            state={state}
            mediaState={mediaState}
            sections={sectionsForDisplay}
            searchTerm={searchTerm}
            onSliderImageChange={handleSliderImageChange}
            onSliderImageRemove={handleSliderImageRemove}
            onDesignImageUpload={handleDesignImageUpload}
            onDesignImageRemove={handleDesignImageRemove}
            onDisableLoader={handleDisableLoader}
            onFieldChange={handleInputChange}
            onResetField={handleResetValue}
            onLinkChange={handleLinkChange}
            onSocialChange={handleSocialChange}
            onSearchChange={onSearchChange}
            onApplySearch={applySearchFilter}
            onProductCheck={onProductCheck}
          />
        </div>
      )}
      {(!isDefaultView || selected === 1) && (
        <div className="d-flex justify-content-center m-3 ">
          {state.isLoading ? (
            <div className={classes.progress}>
              <SpinnerComponent />
            </div>
          ) : (
            <Button onClick={handleSave} className={classes.buttonStyle}>
              ENREGISTRER
            </Button>
          )}
        </div>
      )}
    </>
  );
};

export default connector(withStyles(styles)(withRouter(MobileDesignConfigComponent)));
